import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, Stack, Typography, useTheme } from '@mui/material';
import debounce from 'debounce-promise';
import { type TFunction } from 'i18next';
import memoize from 'memoizee';
import validator from 'validator';

import {
  type GetMidotUsersParams,
  MidotUserDTO,
  useGetMidotUsersHook,
  useGetUsersByIdsHook,
  User,
  UserIdentifier
} from '@/api/generated';
import MailIcon from '@/assets/icons/mail.svg?react';
import DropdownSelect, { DropdownSelectOption, DropdownSelectProps } from '@/components/fields/DropdownSelect';
import { UserAvatarForDropdown } from '@/components/UserAvatar';
import { borders } from '@/theme/constants';
import palette from '@/theme/palette';
import typography from '@/theme/typography';
import { getUserFullName } from '@/utils/user';

const AVATAR_VALUE_SIZE = 20;
const AVATAR_MENU_SIZE = 32;

const DEBOUNCE_DELAY = 300;

export type MidotUserEmailDropdownSelectProps = Omit<DropdownSelectProps, 'options' | 'value' | 'onChange'> & {
  officeSlug?: string[];
  roleId?: string;
  disabledOptions?: string[];
  allowInvites?: boolean;
  onNewEmailCreated?: (email: string) => void;
  isMulti?: boolean;
  /** Values are always UserIdentifier objects: { id } for existing users, { email } for new invites */
  value?: UserIdentifier | UserIdentifier[];
  onChange?: (value: UserIdentifier | UserIdentifier[] | null) => void;
};

const emailToOption = (email: string, t: TFunction) =>
  ({
    label: [t('users:users.invite'), email].join(' '),
    value: email,
    Icon: (
      <Box
        sx={{
          '& .MuiAvatar-root': {
            bgcolor: 'background.default',
            mx: 0
          },
          '[data-context="menu"] & .MuiAvatar-root': {
            width: AVATAR_MENU_SIZE,
            height: AVATAR_MENU_SIZE,
            border: borders.getDash(palette.grey[400]!, borders._0_5px)
          },
          '[data-context="menu"] & .MuiAvatar-root .MailIcon-root': {
            width: 20,
            height: 20,
            color: palette.grey[400]
          },
          '[data-context="value"] & .MuiAvatar-root': {
            width: AVATAR_VALUE_SIZE,
            height: AVATAR_VALUE_SIZE,
            border: borders.getSolid(palette.grey[400]!, borders._1px)
          },
          '[data-context="value"] & .MuiAvatar-root .MailIcon-root': {
            width: 14,
            height: 14,
            color: palette.grey[400]
          }
        }}
      >
        <Avatar sx={{ ml: 0.5, mr: 0.5 }}>
          <MailIcon className="MailIcon-root" />
        </Avatar>
      </Box>
    )
  }) as DropdownSelectOption;

const userToOption = (user: User) => ({
  label: getUserFullName(user),
  labelElement: (
    <Stack gap={0.5}>
      <Typography variant="body2">{getUserFullName(user) || user.email}</Typography>
      <Typography variant="body2" color="text.secondary">
        {user.email}
      </Typography>
    </Stack>
  ),
  value: user.id!,
  Icon: <UserAvatarForDropdown user={user} sx={{ ml: 0.5, mr: 0.5 }} />
});

const filterBySearch = (options: DropdownSelectOption[], inputValue?: string) => {
  if (!inputValue) return options;

  return options.filter(option => (option.label || '').toLowerCase().includes(inputValue?.toLowerCase() || ''));
};

const filterByIds = (options: DropdownSelectOption[], ids?: string[]) => {
  if (!ids) return [];

  const idsSet = new Set(ids);

  return options.filter(option => idsSet.has(option.value));
};

/** Extract string IDs/emails from UserIdentifier values for display in dropdown */
const extractStringValues = (value: UserIdentifier | UserIdentifier[] | null | undefined): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map(v => v.id || v.email).filter((id): id is string => !!id);
  }

  if (value.id) return [value.id];
  if (value.email) return [value.email];

  return [];
};

const MidotUserEmailDropdownSelect: FC<MidotUserEmailDropdownSelectProps> = ({
  officeSlug,
  roleId,
  disabledOptions = [],
  allowInvites = true,
  onNewEmailCreated,
  isMulti = true,
  value,
  onChange,
  ...props
}) => {
  const [emails, setEmails] = useState<string[]>([]);
  const getMidotUsers = useGetMidotUsersHook();
  const getUsersByIds = useGetUsersByIdsHook();
  const { t } = useTranslation('users');
  const { spacing } = useTheme();

  // Convert UserIdentifier values to string IDs/emails for the dropdown
  const stringValue = useMemo(() => extractStringValues(value), [value]);

  const overrideStyles = useMemo(
    () => ({
      multiValue: {
        '& .DropdownSelect-multiValue:not([role=button])': {
          padding: spacing(0.25, 0.5),
          paddingInlineEnd: 0.5
        }
      }
    }),
    [spacing]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadUserOptions = useCallback(
    memoize(
      async (inputValue?: string) => {
        const searchParams: GetMidotUsersParams = { query: inputValue };

        if (officeSlug?.length) {
          searchParams.office_slug = officeSlug;
        }

        if (roleId) {
          searchParams.role_id = roleId;
        }

        const users = await getMidotUsers(searchParams);

        const newUserOptions = (users?.users || []).map((midotUser: MidotUserDTO) => userToOption(midotUser as User));

        const filtered = filterBySearch(newUserOptions, inputValue);

        const presentEmails = emails?.map((email: string) => emailToOption(email, t));

        return [...presentEmails, ...filtered];
      },
      { promise: true }
    ),
    [emails, officeSlug, roleId, t, value]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadUserOptionsDebounced = useCallback(debounce(loadUserOptions, DEBOUNCE_DELAY), [loadUserOptions]);

  const loadUserOptionsByIds = useCallback(
    async (ids?: string[] | string) => {
      // Handle single value (string) or array
      const idsArray = Array.isArray(ids) ? ids : [ids].filter((id): id is string => Boolean(id));
      if (idsArray.length === 0) return [];

      // Separate IDs (for API lookup) from emails (already in local state)
      const userIds = idsArray.filter(id => !validator.isEmail(id));
      const emailValues = idsArray.filter(id => validator.isEmail(id));

      const users = userIds.length > 0 ? await getUsersByIds({ id: userIds }) : [];
      const newUserOptions = (users || []).map((user: User) => userToOption(user));

      const loadedOptions = filterByIds(newUserOptions!, userIds);
      const presentEmails = [...emails, ...emailValues].map((email: string) => emailToOption(email, t));

      return [...loadedOptions, ...presentEmails];
    },
    [emails, t, getUsersByIds]
  );

  const handleAddEmail = useCallback(
    (email: string) => {
      if (!validator.isEmail(email)) return;

      setEmails((oldEmails: string[]) => [...oldEmails, email]);

      // For single-select mode, just set the email as the value
      // For multi-select mode, append to the existing array
      if (!isMulti) {
        onChange?.({ email });
      } else {
        const currentValues = Array.isArray(value) ? value : [value].filter((v): v is UserIdentifier => Boolean(v));
        onChange?.([...currentValues, { email }]);
      }

      onNewEmailCreated?.(email);
    },
    [value, onChange, onNewEmailCreated, isMulti]
  );

  const formatCreateOption = useCallback(
    (inputValue: string) => (
      <Stack direction="row" alignItems="center" className="DropdownSelect-optionLabel">
        <Stack direction="row" alignItems="center" gap={1}>
          <Box className="DropdownSelect-optionIcon">
            <Avatar
              sx={{
                ml: 0.5,
                mr: 0.5,
                width: AVATAR_MENU_SIZE,
                height: AVATAR_MENU_SIZE,
                '&.MuiAvatar-root': {
                  bgcolor: 'background.default'
                },
                '& .MailIcon-root': {
                  width: 20,
                  height: 20,
                  color: palette.grey[400]
                }
              }}
            >
              <MailIcon className="MailIcon-root" />
            </Avatar>
          </Box>
          <Stack direction="row" alignItems="center" className="DropdownSelect-optionText">
            <Typography
              className="DropdownSelect-optionTextPart"
              sx={{
                ...typography.body2,
                color: 'grey.900',
                whiteSpace: 'pre'
              }}
            >
              {t('users:users.invite')}
              &nbsp;
              {inputValue}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    ),
    [t]
  );

  const isValidNewOption = useCallback((inputValue: string) => validator.isEmail(inputValue), []);

  // Convert string values from dropdown to UserIdentifier objects
  const handleChange = useCallback(
    (newValue: string | string[] | null) => {
      if (!onChange) return;

      if (newValue === null) {
        onChange(null);
      } else if (typeof newValue === 'string') {
        // Check if it's an email (new invite) or a user ID
        onChange(validator.isEmail(newValue) ? { email: newValue } : { id: newValue });
      } else {
        // Array of strings → preserve existing UserIdentifier objects where possible
        const currentArray = Array.isArray(value) ? value : [value].filter((v): v is UserIdentifier => Boolean(v));
        const result = newValue.map(v => {
          // Check if we already have this value as a UserIdentifier
          const existing = currentArray.find(cv => cv.id === v || cv.email === v);

          if (existing) return existing;

          // Otherwise, check if it's an email or ID
          return validator.isEmail(v) ? { email: v } : { id: v };
        });
        onChange(result);
      }
    },
    [onChange, value]
  );

  return (
    <DropdownSelect
      {...props}
      value={isMulti ? stringValue : stringValue[0]}
      onChange={handleChange}
      overrideStyles={overrideStyles}
      loadOptions={loadUserOptionsDebounced}
      loadSelectedOptions={loadUserOptionsByIds}
      onCreateOption={handleAddEmail}
      formatCreateOption={formatCreateOption}
      isValidNewOption={isValidNewOption}
      disabledOptions={disabledOptions}
      hideCheckboxes
      useCreatable={allowInvites}
      isMulti={isMulti}
    />
  );
};

export default MidotUserEmailDropdownSelect;
